import { JobOfferEmbedListShowingResponseDto, JobOfferPreview } from "@/lib/interfaces/embed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fingerprintSimilarJobIds, SIMILAR_JOBS_PAGE_SIZE, uniqSimilarJobsById } from "./similar-jobs-helpers";

type ApiRequestFn = <T>(
  route: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  options?: { withoutAuth?: boolean },
  version?: string,
) => Promise<{ data?: T }>;

export function useSimilarJobsList({
  organizationId,
  currentJobId,
  initialSimilarJobs,
  isDebug,
  apiRequest,
}: {
  organizationId: string;
  currentJobId: string;
  initialSimilarJobs?: JobOfferPreview[];
  isDebug?: boolean;
  apiRequest: ApiRequestFn;
}) {
  const [allJobs, setAllJobs] = useState<JobOfferPreview[]>(() =>
    uniqSimilarJobsById((initialSimilarJobs || []).filter((j) => j._id !== currentJobId)),
  );
  const [visibleCount, setVisibleCount] = useState(SIMILAR_JOBS_PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  /** After one embed list fetch we stop offering server load-more (avoids repeat calls). */
  const [hasFetchedOrgList, setHasFetchedOrgList] = useState(false);

  const allJobsRef = useRef(allJobs);
  allJobsRef.current = allJobs;

  const initialSimilarFingerprint = useMemo(
    () => fingerprintSimilarJobIds(initialSimilarJobs),
    [initialSimilarJobs],
  );

  useEffect(() => {
    setHasFetchedOrgList(false);
    if (isDebug) {
      const next = initialSimilarJobs || [];
      setAllJobs(next);
      setVisibleCount(Math.min(SIMILAR_JOBS_PAGE_SIZE, next.length));
      return;
    }
    const next = uniqSimilarJobsById((initialSimilarJobs || []).filter((j) => j._id !== currentJobId));
    setAllJobs(next);
    setVisibleCount(Math.min(SIMILAR_JOBS_PAGE_SIZE, next.length));
    // `initialSimilarJobs` is read from props; `initialSimilarFingerprint` avoids resetting on referentially new arrays with the same ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when job / similar ids change, not on referential churn of `initialSimilarJobs`
  }, [currentJobId, organizationId, isDebug, initialSimilarFingerprint]);

  const visibleJobs = useMemo(() => allJobs.slice(0, visibleCount), [allJobs, visibleCount]);
  const canLoadMore =
    visibleCount < allJobs.length || (!isDebug && !hasFetchedOrgList && allJobs.length >= SIMILAR_JOBS_PAGE_SIZE);

  const onLoadMore = useCallback(async () => {
    if (isLoadingMore) return;

    const total = allJobsRef.current.length;
    if (visibleCount < total) {
      setIsLoadingMore(true);
      setVisibleCount((c) => Math.min(c + SIMILAR_JOBS_PAGE_SIZE, total));
      setTimeout(() => setIsLoadingMore(false), 150);
      return;
    }

    if (isDebug || hasFetchedOrgList) return;

    setIsLoadingMore(true);
    try {
      const res = await apiRequest<JobOfferEmbedListShowingResponseDto>(`embed/${organizationId}/job-offers`, "GET", {
        withoutAuth: true,
      });
      if (!res.data?.jobOffers) {
        setHasFetchedOrgList(true);
        return;
      }

      const mapped = res.data.jobOffers
        .filter((o) => o._id !== currentJobId)
        .map<JobOfferPreview>((o) => ({
          _id: o._id,
          title: o.title,
          hourlySalary: o.hourlySalary,
          location: o.postCode,
          previewImageUrl: o.imageUrl,
          workingTimeModels: o.belongsToWorkingTimeModels,
          showImage: o.showImage,
        }));

      const merged = uniqSimilarJobsById([...allJobsRef.current, ...mapped]);
      setAllJobs(merged);
      setHasFetchedOrgList(true);
      setVisibleCount((c) => Math.min(c + SIMILAR_JOBS_PAGE_SIZE, merged.length));
    } finally {
      setIsLoadingMore(false);
    }
  }, [apiRequest, currentJobId, hasFetchedOrgList, isDebug, isLoadingMore, organizationId, visibleCount]);

  return {
    hasJobs: allJobs.length > 0,
    visibleJobs,
    canLoadMore,
    isLoadingMore,
    onLoadMore,
  };
}
